import { useState, useRef, useEffect } from 'react'
import { useParams } from 'react-router-dom'
import { useSources, type Source } from '../hooks/useSources'
import { useGoal } from '../hooks/useGoal'
import { useTalentNotion, type NotionPage } from '../hooks/useTalentNotion'
import { gettext as _ } from '../lib/i18n'
import Modal from '../components/ui/Modal'
import styles from './SourcesPage.module.css'

const KIND_META: Record<
  Source['kind'],
  { label: string; color: string }
> = {
  resume: { label: '이력서', color: styles.kindIndigo },
  portfolio: { label: '포트폴리오', color: styles.kindViolet },
  project: { label: '프로젝트', color: styles.kindCyan },
  jd: { label: '채용공고', color: styles.kindAmber },
  memo: { label: '메모', color: styles.kindMuted },
}

const TIER_OPTIONS = [
  { key: '네카라쿠배', label: '네카라쿠배' },
  { key: '스타트업', label: '스타트업' },
  { key: '대기업', label: '대기업' },
]

const KIND_OPTIONS = [
  { value: 'resume', label: '이력서' },
  { value: 'portfolio', label: '포트폴리오' },
  { value: 'project', label: '프로젝트' },
  { value: 'jd', label: '채용공고' },
  { value: 'memo', label: '메모' },
]

function GoalCard({
  goal,
  loading,
  saving,
  saved,
  error,
  onSave,
}: {
  goal: ReturnType<typeof useGoal>['goal']
  loading: boolean
  saving: boolean
  saved: boolean
  error: string | null
  onSave: (patch: {
    target_roles?: string[]
    company_tier_pref?: string[]
    style_memo?: string
  }) => void
}) {
  const [targetRoles, setTargetRoles] = useState<string[]>(goal?.targetRoles ?? [])
  const [roleInput, setRoleInput] = useState('')
  const [tiers, setTiers] = useState<string[]>(goal?.companyTierPref ?? [])
  const [styleMemo, setStyleMemo] = useState(goal?.styleMemo ?? '')

  const syncedRef = useRef(false)
  if (goal && !syncedRef.current) {
    setTargetRoles(goal.targetRoles ?? [])
    setTiers(goal.companyTierPref ?? [])
    setStyleMemo(goal.styleMemo ?? '')
    syncedRef.current = true
  }

  const toggleTier = (key: string) => {
    setTiers((prev) =>
      prev.includes(key) ? prev.filter((t) => t !== key) : [...prev, key],
    )
  }

  const addRole = () => {
    const role = roleInput.trim()
    if (!role || targetRoles.includes(role)) return
    setTargetRoles((prev) => [...prev, role])
    setRoleInput('')
  }

  const removeRole = (role: string) => {
    setTargetRoles((prev) => prev.filter((r) => r !== role))
  }

  const handleSave = () => {
    onSave({
      target_roles: targetRoles.length > 0 ? targetRoles : undefined,
      company_tier_pref: tiers.length > 0 ? tiers : undefined,
      style_memo: styleMemo || undefined,
    })
  }

  if (loading) {
    return (
      <div className={styles.card}>
        <div className={styles.skeleton} style={{ height: 160 }} />
      </div>
    )
  }

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{_('Goal Settings')}</h2>
      <p className={styles.cardDesc}>{_('Set this talent\'s career goals')}</p>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{_('Target roles (multiple allowed)')}</label>
        {targetRoles.length > 0 && (
          <div className={styles.chipRow}>
            {targetRoles.map((role) => (
              <span key={role} className={`${styles.chip} ${styles.chipActive}`}>
                {role}
                <button
                  type="button"
                  className={styles.chipRemove}
                  onClick={() => removeRole(role)}
                  aria-label={`${_('Delete')} ${role}`}
                >
                  ×
                </button>
              </span>
            ))}
          </div>
        )}
        <div className={styles.roleInputRow}>
          <input
            className={styles.formInput}
            type="text"
            value={roleInput}
            onChange={(e) => setRoleInput(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault()
                addRole()
              }
            }}
            placeholder={_('e.g. Frontend Developer, PM')}
          />
          <button
            type="button"
            className={styles.addRoleBtn}
            onClick={addRole}
            disabled={!roleInput.trim()}
          >
            {_('Add')}
          </button>
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{_('Preferred company tier')}</label>
        <div className={styles.chipRow}>
          {TIER_OPTIONS.map((opt) => (
            <button
              key={opt.key}
              type="button"
              className={`${styles.chip} ${tiers.includes(opt.key) ? styles.chipActive : ''}`}
              onClick={() => toggleTier(opt.key)}
            >
              {opt.label}
            </button>
          ))}
        </div>
      </div>

      <div className={styles.formGroup}>
        <label className={styles.formLabel}>{_('Style memo')}</label>
        <input
          className={styles.formInput}
          type="text"
          value={styleMemo}
          onChange={(e) => setStyleMemo(e.target.value)}
          placeholder={_('e.g. Honest and concise tone')}
        />
      </div>

      <div className={styles.actionBar}>
        <button
          type="button"
          className={styles.saveBtn}
          disabled={saving}
          onClick={handleSave}
        >
          {saving ? _('Saving...') : _('Save')}
        </button>
        {saved && <span className={styles.savedMsg}>{_('Saved')}</span>}
        {error && <span className={styles.errorMsg}>{error}</span>}
      </div>
    </div>
  )
}

function DropZone({
  uploading,
  progress,
  onFileSelect,
  onFolderSelect,
}: {
  uploading: boolean
  progress: string | null
  onFileSelect: (files: File[]) => void
  onFolderSelect: (files: File[]) => void
}) {
  const fileRef = useRef<HTMLInputElement>(null)
  const folderRef = useRef<HTMLInputElement>(null)

  return (
    <div className={styles.card}>
      <h2 className={styles.cardTitle}>{_('Source Upload')}</h2>
      <p className={styles.cardDesc}>
        {_('Upload resumes, portfolios, job postings. AI analyzes automatically.')}
      </p>

      <div
        className={`${styles.dropzone} ${uploading ? styles.dropzoneUploading : ''}`}
        onClick={() => !uploading && fileRef.current?.click()}
        onDragOver={(e) => {
          e.preventDefault()
          e.stopPropagation()
        }}
        onDrop={(e) => {
          e.preventDefault()
          e.stopPropagation()
          if (uploading) return
          const files = Array.from(e.dataTransfer.files)
          if (files.length > 0) onFileSelect(files)
        }}
      >
        {uploading ? (
          <div className={styles.dropzoneProgress}>
            <div className={styles.spinner} />
            <span>{progress}</span>
          </div>
        ) : (
          <>
            <div className={styles.dropzoneIcon}>📁</div>
            <div className={styles.dropzoneText}>
              {_('Click to select files or drop here')}
            </div>
            <div className={styles.dropzoneHint}>{_('PDF, DOCX, TXT etc')}</div>
          </>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        multiple
        className={styles.hiddenInput}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onFileSelect(files)
          e.target.value = ''
        }}
      />

      <button
        type="button"
        className={styles.folderBtn}
        disabled={uploading}
        onClick={() => folderRef.current?.click()}
      >
        {_('Bulk folder upload')}
      </button>
      <input
        ref={folderRef}
        type="file"
        multiple
        {...({ webkitdirectory: '', directory: '' } as any)}
        className={styles.hiddenInput}
        onChange={(e) => {
          const files = Array.from(e.target.files ?? [])
          if (files.length > 0) onFolderSelect(files)
          e.target.value = ''
        }}
      />
    </div>
  )
}

function SourceItem({
  source,
  expanded,
  onToggle,
  onDelete,
}: {
  source: Source
  expanded: boolean
  onToggle: () => void
  onDelete: (id: string) => void
}) {
  const meta = KIND_META[source.kind] ?? KIND_META.memo

  return (
    <div
      className={`${styles.sourceItem} ${expanded ? styles.sourceItemExpanded : ''}`}
      onClick={onToggle}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault()
          onToggle()
        }
      }}
    >
      <div className={`${styles.kindDot} ${meta.color}`} />
      <div className={styles.sourceInfo}>
        <div className={styles.sourceTitle}>{source.title}</div>
        {source.summary && (
          <div className={styles.sourceSummary}>{source.summary}</div>
        )}
        {source.keywords.length > 0 && (
          <div className={styles.keywordRow}>
            {source.keywords.map((kw) => (
              <span key={kw} className={styles.keywordChip}>
                {kw}
              </span>
            ))}
          </div>
        )}

        {expanded && (
          <div className={styles.detailPanel}>
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{_('Kind')}</span>
              <span className={styles.detailValue}>{meta.label}</span>
            </div>
            {source.sourceUrl && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{_('Source URL')}</span>
                <a
                  className={styles.detailLink}
                  href={source.sourceUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                >
                  {source.sourceUrl} ↗
                </a>
              </div>
            )}
            {source.r2Key && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{_('R2 Key')}</span>
                <span className={styles.detailValueMono}>{source.r2Key}</span>
              </div>
            )}
            <div className={styles.detailRow}>
              <span className={styles.detailLabel}>{_('Created')}</span>
              <span className={styles.detailValueMono}>
                {new Date(source.createdAt).toLocaleString('ko-KR')}
              </span>
            </div>
            {source.contentText && (
              <div className={styles.detailRow}>
                <span className={styles.detailLabel}>{_('Content preview')}</span>
                <span className={styles.detailContent}>
                  {source.contentText.slice(0, 500)}
                  {source.contentText.length > 500 ? '…' : ''}
                </span>
              </div>
            )}
          </div>
        )}
      </div>
      <span className={`${styles.kindBadge} ${meta.color}`}>{meta.label}</span>
      <button
        type="button"
        className={styles.deleteBtn}
        onClick={(e) => {
          e.stopPropagation()
          onDelete(source.id)
        }}
        title={_('Delete')}
      >
        ✕
      </button>
    </div>
  )
}

function NotionImportModal({
  open,
  onClose,
  importing,
  notionConnected,
  pages,
  pagesLoading,
  pagesError,
  onLoadPages,
  onImport,
}: {
  open: boolean
  onClose: () => void
  importing: boolean
  notionConnected: boolean
  pages: NotionPage[]
  pagesLoading: boolean
  pagesError: string | null
  onLoadPages: () => void
  onImport: (pageId: string) => Promise<boolean>
}) {
  const [selectedPage, setSelectedPage] = useState<string | null>(null)

  useEffect(() => {
    if (open && notionConnected) {
      onLoadPages()
      setSelectedPage(null)
    }
  }, [open, notionConnected, onLoadPages])

  const handleImport = async () => {
    if (!selectedPage) return
    const ok = await onImport(selectedPage)
    if (ok) onClose()
  }

  return (
    <Modal open={open} onClose={onClose} title={_('Import from Notion')} size="lg">
      {!notionConnected ? (
        <div className={styles.notionNotConnected}>
          <p>{_('Notion is not connected. Please connect in Notion Sync page first.')}</p>
        </div>
      ) : pagesLoading ? (
        <div className={styles.notionPageLoading}>
          <div className={styles.spinner} />
          <span>{_('Loading Notion pages...')}</span>
        </div>
      ) : pagesError ? (
        <div className={styles.notionPageError}>
          <span className={styles.errorMsg}>{pagesError}</span>
          <button className={styles.retryBtn} onClick={onLoadPages}>
            {_('Retry')}
          </button>
        </div>
      ) : pages.length === 0 ? (
        <div className={styles.notionPageEmpty}>
          <div className={styles.emptyStateIcon}>📄</div>
          <div>{_('No Notion pages found')}</div>
        </div>
      ) : (
        <>
          <div className={styles.notionPageList}>
            {pages.map((page) => (
              <label
                key={page.id}
                className={`${styles.notionPageItem} ${selectedPage === page.id ? styles.notionPageItemSelected : ''}`}
              >
                <input
                  type="radio"
                  name="notion-page"
                  checked={selectedPage === page.id}
                  onChange={() => setSelectedPage(page.id)}
                />
                <div className={styles.notionPageInfo}>
                  <div className={styles.notionPageTitle}>{page.title}</div>
                  <a
                    href={page.url}
                    target="_blank"
                    rel="noopener noreferrer"
                    onClick={(e) => e.stopPropagation()}
                    className={styles.notionPageLink}
                  >
                    {_('Open in Notion')} ↗
                  </a>
                </div>
              </label>
            ))}
          </div>
          <div className={styles.notionImportActions}>
            <button
              type="button"
              className={styles.cancelBtn}
              onClick={onClose}
              disabled={importing}
            >
              {_('Cancel')}
            </button>
            <button
              type="button"
              className={styles.saveBtn}
              onClick={handleImport}
              disabled={!selectedPage || importing}
            >
              {importing ? _('Importing...') : _('Import')}
            </button>
          </div>
        </>
      )}
    </Modal>
  )
}

export default function SourcesPage() {
  const { talentId } = useParams<{ talentId: string }>()
  const id = talentId ?? null

  const { sources, loading, error, uploading, uploadProgress, reload, uploadSingle, uploadBulk, remove, importFromNotion } =
    useSources(id)
  const { goal, loading: goalLoading, saving, saved, error: goalError, save } = useGoal(id)
  const { status: notionStatus, pages: notionPages, pagesLoading: notionPagesLoading, pagesError: notionPagesError, loadPages: loadNotionPages } = useTalentNotion(id)

  const [bulkKind, setBulkKind] = useState('resume')
  const [selectedId, setSelectedId] = useState<string | null>(null)
  const [showImportModal, setShowImportModal] = useState(false)
  const [importing, setImporting] = useState(false)

  const handleFileSelect = async (files: File[]) => {
    if (files.length === 1) {
      await uploadSingle(files[0], {
        kind: bulkKind,
        title: files[0].name,
      })
    } else {
      await uploadBulk(files, bulkKind)
    }
  }

  const handleFolderSelect = async (files: File[]) => {
    await uploadBulk(files, bulkKind)
  }

  const handleImport = async (pageId: string): Promise<boolean> => {
    setImporting(true)
    try {
      const ok = await importFromNotion(pageId, bulkKind)
      return ok
    } finally {
      setImporting(false)
    }
  }

  if (!id) {
    return (
      <div className={styles.emptyWrap}>
        <div className={styles.emptyIcon}>👤</div>
        <div>{_('Select a talent')}</div>
      </div>
    )
  }

  return (
    <div className={styles.page}>
      <h1 className={styles.title}>{_('Sources')}</h1>

      <GoalCard
        goal={goal}
        loading={goalLoading}
        saving={saving}
        saved={saved}
        error={goalError}
        onSave={save}
      />

      <div className={styles.card}>
        <div className={styles.formGroup}>
          <label className={styles.formLabel}>{_('File type')}</label>
          <select
            className={styles.formInput}
            value={bulkKind}
            onChange={(e) => setBulkKind(e.target.value)}
          >
            {KIND_OPTIONS.map((k) => (
              <option key={k.value} value={k.value}>
                {k.label}
              </option>
            ))}
          </select>
        </div>
      </div>

      <DropZone
        uploading={uploading}
        progress={uploadProgress}
        onFileSelect={handleFileSelect}
        onFolderSelect={handleFolderSelect}
      />

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{_('Import from Notion')}</h2>
        <p className={styles.cardDesc}>
          {_('Import a Notion page as a source material. AI analyzes automatically.')}
        </p>
        <button
          type="button"
          className={styles.folderBtn}
          onClick={() => setShowImportModal(true)}
          disabled={uploading}
        >
          {_('Select Notion page')}
        </button>
      </div>

      <NotionImportModal
        open={showImportModal}
        onClose={() => setShowImportModal(false)}
        importing={importing}
        notionConnected={notionStatus?.connected ?? false}
        pages={notionPages}
        pagesLoading={notionPagesLoading}
        pagesError={notionPagesError}
        onLoadPages={loadNotionPages}
        onImport={handleImport}
      />

      <div className={styles.card}>
        <h2 className={styles.cardTitle}>{_('Source List')}</h2>
        <p className={styles.cardDesc}>
          {_('Manage uploaded source materials')}
        </p>

        {loading ? (
          <div className={styles.skeleton} style={{ height: 120 }} />
        ) : error ? (
          <div className={styles.errorWrap}>
            <span className={styles.errorMsg}>{error}</span>
            <button className={styles.retryBtn} onClick={reload}>
              {_('Retry')}
            </button>
          </div>
        ) : sources.length === 0 ? (
          <div className={styles.emptyState}>
            <div className={styles.emptyStateIcon}>📂</div>
            <div>{_('Drop files and AI will analyze')}</div>
          </div>
        ) : (
          <div className={styles.sourceList}>
            {sources.map((s) => (
              <SourceItem
                key={s.id}
                source={s}
                expanded={selectedId === s.id}
                onToggle={() =>
                  setSelectedId(selectedId === s.id ? null : s.id)
                }
                onDelete={remove}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  )
}
